import React from 'react';
import { Progress } from '@/components/ui/progress';
import { 
  calculatePasswordStrength, 
  getPasswordStrengthLabel, 
  getPasswordStrengthColor 
} from '@/utils/passwordValidation';
import { Check, X, AlertCircle } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password: string;
  showDetails?: boolean;
  className?: string;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  showDetails = true,
  className = '',
}) => {
  if (!password) {
    return null;
  }

  const strength = calculatePasswordStrength(password);
  const strengthLabel = getPasswordStrengthLabel(strength.score);
  const strengthColor = getPasswordStrengthColor(strength.score);
  
  // Convert score (0-4) to percentage for progress bar
  const progressValue = (strength.score / 4) * 100;

  // Get progress bar color class
  const getProgressColor = (score: number) => {
    switch (score) {
      case 0:
      case 1:
        return 'bg-red-500';
      case 2:
        return 'bg-yellow-500';
      case 3:
        return 'bg-blue-500';
      case 4:
        return 'bg-green-500';
      default:
        return 'bg-gray-300';
    }
  };

  const requirements = [
    { met: password.length >= 8, label: 'At least 8 characters' },
    { met: /[a-z]/.test(password), label: 'Lowercase letter' },
    { met: /[A-Z]/.test(password), label: 'Uppercase letter' },
    { met: /[0-9]/.test(password), label: 'Number' },
    { met: /[^a-zA-Z0-9]/.test(password), label: 'Special character' },
  ];

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Progress bar and label */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Password strength:</span>
        <span className={`text-sm font-medium ${strengthColor}`}>
          {strengthLabel}
        </span>
      </div>
      
      <div className="relative">
        <Progress 
          value={progressValue} 
          className="h-2"
        />
        <div 
          className={`absolute inset-0 h-2 rounded-full transition-all duration-300 ${getProgressColor(strength.score)}`}
          style={{ width: `${progressValue}%` }}
        />
      </div>

      {/* Detailed feedback */}
      {showDetails && (
        <div className="space-y-2">
          {/* Requirements checklist */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
            {requirements.map((req, index) => (
              <div key={index} className="flex items-center gap-1">
                {req.met ? (
                  <Check className="h-3 w-3 text-green-500 shrink-0" />
                ) : (
                  <X className="h-3 w-3 text-muted-foreground shrink-0" />
                )}
                <span className={req.met ? 'text-green-600' : 'text-muted-foreground'}>
                  {req.label}
                </span>
              </div>
            ))}
          </div>

          {/* Feedback suggestions */}
          {strength.feedback.length > 0 && (
            <div className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
              <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="text-xs font-medium text-yellow-800">Suggestions:</p>
                <ul className="text-xs text-yellow-700 space-y-0.5">
                  {strength.feedback.map((suggestion, index) => (
                    <li key={index}>• {suggestion}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Security tips for strong passwords */}
          {strength.score >= 3 && (
            <div className="flex items-start gap-2 p-2 bg-green-50 border border-green-200 rounded-md">
              <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
              <p className="text-xs text-green-700">
                Great! Your password is {strengthLabel.toLowerCase()}. 
                {strength.score === 4 && ' Consider using a password manager for even better security.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;